import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, MapPin, Wrench, DollarSign, Volume2, ClipboardList,
  Shield, Clock, ArrowRight, Save, Loader2, RotateCcw, AlertTriangle, Plus, Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast, Toaster } from 'sonner';
import AdminStatusStrip from '../components/AdminStatusStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/company-brain`;

const SECTIONS = [
  { id: 'basics',     label: 'Business basics',     Icon: Building2 },
  { id: 'services',   label: 'Services',            Icon: Wrench },
  { id: 'pricing',    label: 'Pricing / quote',     Icon: DollarSign },
  { id: 'tone',       label: 'Tone & style',        Icon: Volume2 },
  { id: 'qualify',    label: 'Qualification',       Icon: ClipboardList },
  { id: 'followup',   label: 'Follow-up',           Icon: Clock },
  { id: 'safety',     label: 'Safety & handoff',    Icon: Shield },
];

const EMPTY_BRAIN = {
  name: '',
  industry: '',
  service_area: [],
  services_offered: [],
  services_not_offered: [],
  pricing_policy: '',
  quote_policy: '',
  preferred_tone: '',
  response_length: 'medium',
  working_hours: '',
  after_hours_policy: '',
  qualification_questions: [],
  required_disclaimers: [],
  follow_up_cadence: [],
  handoff_instructions: '',
  version: 0,
  updatedAt: null,
  updatedBy: null,
};

const CompanyBrain = () => {
  useEffect(() => { document.title = 'HussarAi · Company Brain'; }, []);
  const [brain, setBrain] = useState(EMPTY_BRAIN);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('basics');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(API, { withCredentials: true });
      setBrain({ ...EMPTY_BRAIN, ...r.data });
    } catch {
      toast.error('Failed to load Company Brain');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setBrain((b) => ({ ...b, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(API, brain, { withCredentials: true });
      setBrain({ ...EMPTY_BRAIN, ...r.data });
      toast.success(`Saved · version v${r.data.version}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || 'Save failed');
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Restore the default HVAC Wrocław brain? Current version will be archived.')) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/reset-to-default`, {}, { withCredentials: true });
      setBrain({ ...EMPTY_BRAIN, ...r.data });
      toast.success(`Reset to default · version v${r.data.version}`);
    } catch {
      toast.error('Reset failed');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="hussar-tc-page">
        <main className="hussar-tc-main">
          <Loader2 className="hussar-tc-spinner animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="hussar-tc-page" data-testid="company-brain-page">
      <Toaster richColors position="top-center" />
      <header className="hussar-tc-header">
        <div className="hussar-tc-header-inner">
          <div>
            <Link to="/admin/training-center" className="hussar-tc-back" data-testid="cb-back">← Training Center</Link>
            <h1 className="hussar-tc-h1" data-testid="cb-h1">Company Brain</h1>
            <p className="hussar-tc-sub">
              Onboarding layer for this business. Injected into every lead-audit
              generation alongside Founder Doctrine, agent prompts, tone rules,
              and the industry playbook.
            </p>
            <p className="hussar-tc-sub" data-testid="cb-version">
              Current version: <strong>v{brain.version || 0}</strong>
              {brain.updatedAt && (
                <> · Updated {new Date(brain.updatedAt).toLocaleString()} by {brain.updatedBy || '—'}</>
              )}
            </p>
          </div>
          <div className="hussar-tc-badge" data-testid="cb-admin-badge">
            <AlertTriangle className="w-4 h-4" /> Admin only · Not exposed to public users
          </div>
        </div>
        <nav className="hussar-tc-tabs" data-testid="cb-tabs">
          {SECTIONS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`hussar-tc-tab ${tab === id ? 'is-active' : ''}`}
              data-testid={`cb-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </header>

      <main className="hussar-tc-main">
        <AdminStatusStrip />
        {tab === 'basics'   && <BasicsTab brain={brain} set={set} />}
        {tab === 'services' && <ServicesTab brain={brain} set={set} />}
        {tab === 'pricing'  && <PricingTab brain={brain} set={set} />}
        {tab === 'tone'     && <ToneTab brain={brain} set={set} />}
        {tab === 'qualify'  && <QualifyTab brain={brain} set={set} />}
        {tab === 'followup' && <FollowupTab brain={brain} set={set} />}
        {tab === 'safety'   && <SafetyTab brain={brain} set={set} />}

        <Card className="hussar-tc-card hussar-cb-actions" data-testid="cb-actions">
          <div className="hussar-cb-actions-row">
            <Button onClick={save} disabled={saving} className="hussar-btn-primary" data-testid="cb-save">
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Company Brain'}
            </Button>
            <Button onClick={resetToDefault} variant="outline" disabled={saving} data-testid="cb-reset">
              <RotateCcw className="w-4 h-4" /> Reset to default HVAC brain
            </Button>
            <Link to="/admin/training-center" className="hussar-tc-back" data-testid="cb-link-training">
              <ArrowRight className="w-4 h-4" /> Back to Training Center
            </Link>
          </div>
        </Card>
      </main>
    </div>
  );
};


/* ---------- Reusable list editor ---------- */
const ListField = ({ label, items, onChange, placeholder, testid, type = 'text' }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = type === 'number' ? Number(draft) : draft.trim();
    if (type === 'number') {
      if (!Number.isFinite(v) || v <= 0) return;
    } else if (!v) return;
    onChange([...items, v]);
    setDraft('');
  };
  return (
    <div className="hussar-cb-field" data-testid={testid}>
      <label className="hussar-form-label">{label}</label>
      <ul className="hussar-cb-list">
        {items.map((it, idx) => (
          // DEFERRED (P2): mutable user-edited list with no stable IDs
          // (items may be duplicate empty strings while typing). Matches
          // the deferred pattern in TrainingCenter — fix together when the
          // CompanyBrain API gains stable item IDs.
          // eslint-disable-next-line react/no-array-index-key
          <li key={idx} className="hussar-cb-list-item">
            <span>{String(it)}</span>
            <button
              type="button"
              className="hussar-cb-list-remove"
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              data-testid={`${testid}-remove-${idx}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </li>
        ))}
      </ul>
      <div className="hussar-cb-list-add">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          type={type}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          data-testid={`${testid}-input`}
        />
        <Button type="button" variant="outline" onClick={add} data-testid={`${testid}-add`}>
          <Plus className="w-4 h-4" /> Add
        </Button>
      </div>
    </div>
  );
};


/* ---------- Section components ---------- */
const BasicsTab = ({ brain, set }) => (
  <Card className="hussar-tc-card" data-testid="cb-basics">
    <h2 className="hussar-tc-h2">Business basics</h2>
    <label className="hussar-form-label">Company name</label>
    <Input value={brain.name} onChange={(e) => set('name', e.target.value)} data-testid="cb-name" />
    <label className="hussar-form-label">Industry (slug)</label>
    <Input value={brain.industry} onChange={(e) => set('industry', e.target.value)} placeholder="hvac" data-testid="cb-industry" />
    <ListField label="Service area / cities" items={brain.service_area || []}
               onChange={(v) => set('service_area', v)} placeholder="Wrocław…"
               testid="cb-service-area" />
    <label className="hussar-form-label">Working hours</label>
    <Input value={brain.working_hours} onChange={(e) => set('working_hours', e.target.value)} data-testid="cb-working-hours" />
  </Card>
);

const ServicesTab = ({ brain, set }) => (
  <Card className="hussar-tc-card" data-testid="cb-services">
    <h2 className="hussar-tc-h2">Services</h2>
    <ListField label="Services offered" items={brain.services_offered || []}
               onChange={(v) => set('services_offered', v)} placeholder="klimatyzacja split"
               testid="cb-services-offered" />
    <ListField label="Services NOT offered" items={brain.services_not_offered || []}
               onChange={(v) => set('services_not_offered', v)} placeholder="klimatyzacja przemysłowa"
               testid="cb-services-not-offered" />
  </Card>
);

const PricingTab = ({ brain, set }) => (
  <Card className="hussar-tc-card" data-testid="cb-pricing">
    <h2 className="hussar-tc-h2">Pricing / quote</h2>
    <label className="hussar-form-label">Pricing policy</label>
    <Textarea rows={4} value={brain.pricing_policy} onChange={(e) => set('pricing_policy', e.target.value)} data-testid="cb-pricing-policy" />
    <label className="hussar-form-label">Quote policy</label>
    <Textarea rows={4} value={brain.quote_policy} onChange={(e) => set('quote_policy', e.target.value)} data-testid="cb-quote-policy" />
  </Card>
);

const ToneTab = ({ brain, set }) => (
  <Card className="hussar-tc-card" data-testid="cb-tone">
    <h2 className="hussar-tc-h2">Tone & style</h2>
    <label className="hussar-form-label">Preferred tone</label>
    <Textarea rows={3} value={brain.preferred_tone} onChange={(e) => set('preferred_tone', e.target.value)} data-testid="cb-preferred-tone" />
    <label className="hussar-form-label">Response length</label>
    <Select value={brain.response_length || 'medium'} onValueChange={(v) => set('response_length', v)}>
      <SelectTrigger data-testid="cb-response-length"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="short">short</SelectItem>
        <SelectItem value="medium">medium</SelectItem>
        <SelectItem value="long">long</SelectItem>
      </SelectContent>
    </Select>
  </Card>
);

const QualifyTab = ({ brain, set }) => (
  <Card className="hussar-tc-card" data-testid="cb-qualify">
    <h2 className="hussar-tc-h2">Qualification</h2>
    <ListField label="Required qualification questions (drive `missing_info`)"
               items={brain.qualification_questions || []}
               onChange={(v) => set('qualification_questions', v)}
               placeholder="Metraż / liczba pomieszczeń"
               testid="cb-qualify-q" />
  </Card>
);

const FollowupTab = ({ brain, set }) => (
  <Card className="hussar-tc-card" data-testid="cb-followup">
    <h2 className="hussar-tc-h2">Follow-up</h2>
    <ListField label="Follow-up cadence (days)" items={brain.follow_up_cadence || []}
               onChange={(v) => set('follow_up_cadence', v)} placeholder="3"
               testid="cb-followup-days" type="number" />
  </Card>
);

const SafetyTab = ({ brain, set }) => (
  <Card className="hussar-tc-card" data-testid="cb-safety">
    <h2 className="hussar-tc-h2">Safety & handoff</h2>
    <label className="hussar-form-label">After-hours / emergency policy</label>
    <Textarea rows={3} value={brain.after_hours_policy} onChange={(e) => set('after_hours_policy', e.target.value)} data-testid="cb-after-hours" />
    <ListField label="Required disclaimers" items={brain.required_disclaimers || []}
               onChange={(v) => set('required_disclaimers', v)}
               placeholder="Dokładna wycena po krótkiej rozmowie technicznej."
               testid="cb-disclaimers" />
    <label className="hussar-form-label">Contact handoff instructions</label>
    <Textarea rows={3} value={brain.handoff_instructions} onChange={(e) => set('handoff_instructions', e.target.value)} data-testid="cb-handoff" />
  </Card>
);

export default CompanyBrain;
